import React, { useState } from 'react';
import { ListTodo, Plus, X, Star, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';

export const TodoList = ({ todos = [], priorityTodos = [], onUpdate }) => {
  const [newTodo, setNewTodo] = useState('');

  const addTodo = () => {
    if (!newTodo.trim()) return;
    
    const todo = {
      id: Date.now(),
      text: newTodo.trim(),
      completed: false,
      isPriority: false
    };
    
    onUpdate({
      todos: [...todos, todo],
      priorityTodos
    });
    setNewTodo('');
  };

  const toggleTodo = (id) => {
    const updatedTodos = todos.map(t => 
      t.id === id ? { ...t, completed: !t.completed } : t
    );
    onUpdate({ todos: updatedTodos, priorityTodos });
  };

  const togglePriority = (id) => {
    const todo = todos.find(t => t.id === id);
    if (!todo) return;

    if (todo.isPriority) {
      // Remove from priority
      const updatedTodos = todos.map(t => 
        t.id === id ? { ...t, isPriority: false } : t
      );
      const updatedPriority = priorityTodos.filter(t => t.id !== id);
      onUpdate({ todos: updatedTodos, priorityTodos: updatedPriority });
    } else {
      // Add to priority (max 3)
      if (priorityTodos.length >= 3) return;
      
      const updatedTodos = todos.map(t => 
        t.id === id ? { ...t, isPriority: true } : t
      );
      const updatedPriority = [...priorityTodos, { ...todo, isPriority: true }];
      onUpdate({ todos: updatedTodos, priorityTodos: updatedPriority });
    }
  };

  const removeTodo = (id) => {
    const updatedTodos = todos.filter(t => t.id !== id);
    const updatedPriority = priorityTodos.filter(t => t.id !== id);
    onUpdate({ todos: updatedTodos, priorityTodos: updatedPriority });
  };

  const completedCount = todos.filter(t => t.completed).length;
  const completionPercent = todos.length > 0 ? Math.round((completedCount / todos.length) * 100) : 0;

  return (
    <div className="glass-card p-6" data-testid="todo-list">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <ListTodo className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Today's Tasks</h3>
            <p className="text-xs text-white/50">{completedCount}/{todos.length} completed</p>
          </div>
        </div>
        <span className="font-mono text-2xl text-purple-400">
          {completionPercent}%
        </span>
      </div>

      {/* Priority Section */}
      {priorityTodos.length > 0 && (
        <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            <span className="text-xs font-medium text-amber-400 uppercase tracking-wide">
              Non-Negotiables ({priorityTodos.length}/3)
            </span>
          </div>
          <div className="space-y-2">
            {priorityTodos.map(todo => (
              <div 
                key={todo.id} 
                className={`flex items-center gap-3 p-2 rounded-lg ${
                  todo.completed ? 'opacity-50' : ''
                }`}
              >
                <Checkbox
                  checked={todo.completed}
                  onCheckedChange={() => toggleTodo(todo.id)}
                  className="border-amber-400 data-[state=checked]:bg-amber-400"
                  data-testid={`priority-todo-check-${todo.id}`}
                />
                <span className={`flex-1 text-sm text-white ${
                  todo.completed ? 'line-through' : ''
                }`}>
                  {todo.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Todo Input */}
      <div className="flex gap-2 mb-4">
        <Input
          value={newTodo}
          onChange={(e) => setNewTodo(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addTodo()}
          placeholder="Add a task..."
          className="input-futuristic flex-1"
          data-testid="new-todo-input"
        />
        <Button 
          onClick={addTodo}
          className="h-12 w-12 rounded-xl bg-purple-500/20 hover:bg-purple-500/30 text-purple-400"
          data-testid="add-todo-btn"
        >
          <Plus className="w-5 h-5" />
        </Button>
      </div>

      {/* Todo List */}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {todos.filter(t => !t.isPriority).map(todo => (
          <div 
            key={todo.id} 
            className={`flex items-center gap-3 p-3 rounded-xl bg-white/5 group transition-all duration-200 hover:bg-white/10 ${
              todo.completed ? 'opacity-50' : ''
            }`}
          >
            <Checkbox
              checked={todo.completed}
              onCheckedChange={() => toggleTodo(todo.id)}
              className="border-white/30 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400"
              data-testid={`todo-check-${todo.id}`}
            />
            <span className={`flex-1 text-sm text-white/90 ${
              todo.completed ? 'line-through' : ''
            }`}>
              {todo.text}
            </span>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => togglePriority(todo.id)}
                className={`h-8 w-8 p-0 rounded-lg ${
                  priorityTodos.length >= 3 && !todo.isPriority
                    ? 'opacity-30 cursor-not-allowed'
                    : 'hover:bg-amber-500/20 text-amber-400'
                }`}
                disabled={priorityTodos.length >= 3 && !todo.isPriority}
                data-testid={`todo-priority-${todo.id}`}
              >
                <Star className={`w-4 h-4 ${todo.isPriority ? 'fill-amber-400' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeTodo(todo.id)}
                className="h-8 w-8 p-0 rounded-lg hover:bg-red-500/20 text-red-400"
                data-testid={`todo-delete-${todo.id}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
        
        {todos.length === 0 && (
          <div className="text-center py-8 text-white/30">
            <ListTodo className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No tasks yet. Add one above!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TodoList;
